import { Metadata } from 'next';

export type LocalizedSlugs = Record<string, string>;

export interface MetadataConfig {
  // Site configuration
  siteName: string;
  siteUrl: string;
  defaultDescription: string;

  // Locale configuration
  languageTags: Record<string, string>;
}

export interface MetadataOptions {
  title?: string;
  description?: string;
  image?: string;
  locale?: string;
  slug?: string;
  keywords?: string[];
  authors?: { name: string; url?: string }[];
  creator?: string;
  publisher?: string;
  localizedSlugs?: LocalizedSlugs;
  other?: Record<string, any>;
  // OpenGraph and Twitter specific options
  ogType?: 'website' | 'article' | 'book' | 'profile';
  publishedTime?: string;
  modifiedTime?: string;
  articleAuthor?: string;
  articleSection?: string;
  articleTag?: string[];
  twitterCreator?: string;
}

export function createMetadataGenerator(config: MetadataConfig) {
  return function generateMetadata(options: MetadataOptions): Metadata {
    const {
      title,
      description,
      image,
      locale,
      slug,
      keywords,
      authors,
      creator,
      publisher,
      localizedSlugs,
      other,
      ogType = 'website',
      publishedTime,
      modifiedTime,
      articleAuthor,
      articleSection,
      articleTag,
      twitterCreator,
    } = options;

    const pageTitle = title ? `${title} | ${config.siteName}` : config.siteName;
    const pageDescription = description || config.defaultDescription;
    const isHomePage = localizedSlugs && locale && localizedSlugs[locale] === '/';
    const canonicalSlug = isHomePage ? '' : slug;

    const metadata: Metadata = {
      title: pageTitle,
      description: pageDescription,
      metadataBase: new URL(config.siteUrl),
      alternates: {
        canonical:
          locale === 'en'
            ? `${canonicalSlug ? `/${canonicalSlug}` : '/'}`
            : `/${locale}${canonicalSlug ? `/${canonicalSlug}` : ''}`,
        languages: localizedSlugs
          ? Object.entries(config.languageTags).reduce(
              (acc, [locale, tag]) => {
                const slug = localizedSlugs[locale];
                if (slug && slug !== '') {
                  if (locale === 'en') {
                    acc[tag] = slug === '/' ? '/' : `/${slug}`;
                  } else {
                    acc[tag] = `/${locale}${slug === '/' ? '' : `/${slug}`}`;
                  }
                }
                return acc;
              },
              {} as Record<string, string>
            )
          : undefined,
      },
      openGraph: {
        title: pageTitle,
        description: pageDescription,
        images: image ? [{ url: image }] : undefined,
        url:
          config.siteUrl +
          (locale === 'en'
            ? `${canonicalSlug ? `/${canonicalSlug}` : '/'}`
            : `/${locale}${canonicalSlug ? `/${canonicalSlug}` : ''}`),
        siteName: config.siteName,
        locale: locale,
        type: ogType,
        ...(ogType === 'article' && {
          publishedTime,
          modifiedTime,
          authors: articleAuthor ? [articleAuthor] : undefined,
          section: articleSection,
          tags: articleTag,
        }),
      },
      twitter: {
        card: 'summary_large_image',
        title: pageTitle,
        description: pageDescription,
        images: image ? [image] : undefined,
        site: '@Roblox',
        creator: twitterCreator,
      },
    };

    if (keywords) {
      metadata.keywords = keywords;
    }

    if (authors) {
      metadata.authors = authors;
    }

    if (creator) {
      metadata.creator = creator;
    }

    if (publisher) {
      metadata.publisher = publisher;
    }

    if (other) {
      metadata.other = other;
    }

    return metadata;
  };
}
